import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Building2, Users, BarChart3, LogOut, Menu, X, UserCircle, Settings, GitCompare, Calendar, FileText, Cog, Bell, UserPlus, MessageSquare, Search, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_realtor-dash-3/artifacts/r98q2q3d_Capin%20Realty%20Logo.jpg";

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isGuest = user.role === "guest";
  const isSM = user.role === "sourcing_manager";

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (isGuest || user.role === 'user') return;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotificationCount(response.data.unread_count);
      } catch (error) {
        // Silently fail
      }
    };
    fetchNotificationCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [isGuest, user.role]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      toast.error("Failed to load notifications");
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setNotificationCount(Math.max(0, notificationCount - 1));
    } catch (error) {
      // Silently fail
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setNotificationCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setNotificationCount(0);
      toast.success("All notifications cleared");
    } catch (error) {
      toast.error("Failed to clear notifications");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success(isGuest ? "Guest session ended" : "Logged out successfully");
    navigate("/login");
  };

  // Check if user is a regular user or guest (same access level)
  const isRegularUser = user.role === "user";
  const isViewOnlyUser = isGuest || isRegularUser;
  const isTeamLeader = user.role === "team_leader";
  const isAdmin = user.role === "admin";

  // Build navigation based on user role
  let navigation = [];
  const isSourcingManager = user.role === 'sourcing_manager';
  const canAddProperty = !isGuest && !isRegularUser; // Only admin, agent, team_leader, sourcing_manager can add
  
  if (isViewOnlyUser) {
    // Guest and Property Seekers - only search, no add
    navigation = [
      { name: "Search Properties", path: "/properties", icon: Search },
      { name: "Compare", path: "/properties/compare", icon: GitCompare },
      { name: "Feedback", path: "/feedback", icon: MessageSquare },
    ];
    
    if (!isGuest) {
      navigation.push({ name: "My Profile", path: "/profile", icon: Settings });
    }
  } else {
    // Team members - separate search and add
    navigation = [
      { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
      { name: "Search Properties", path: "/properties", icon: Search },
      { name: "Add Property", path: "/properties/new", icon: PlusCircle },
      { name: "Leads", path: "/leads", icon: Users },
      { name: "Calendar", path: "/calendar", icon: Calendar },
      { name: "Documents", path: "/documents", icon: FileText },
      { name: "Reports", path: "/reports", icon: BarChart3 },
    ];
    
    if (isAdmin) {
      navigation.push({ name: "Team", path: "/users", icon: UserCircle });
      navigation.push({ name: "Property Seekers", path: "/property-seekers", icon: UserPlus });
      navigation.push({ name: "Reviews & Analytics", path: "/feedback", icon: MessageSquare });
      navigation.push({ name: "Settings", path: "/admin/settings", icon: Cog });
    }
    
    // Sourcing Manager gets a special profile page
    if (isSourcingManager) {
      navigation.push({ name: "My Profile", path: "/my-profile", icon: Settings });
    } else {
      navigation.push({ name: "Profile", path: "/profile", icon: Settings });
    }
  }

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin": return "bg-gradient-to-r from-[#c9a227] to-[#ae8220] text-[#0f2744]";
      case "team_leader": return "bg-gradient-to-r from-purple-500 to-purple-700 text-white";
      case "agent": return "bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white";
      case "sourcing_manager": return "bg-gradient-to-r from-emerald-500 to-emerald-700 text-white";
      case "user": return "bg-blue-100 text-blue-800";
      case "guest": return "bg-amber-100 text-amber-800";
      default: return "bg-slate-100 text-slate-800";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "team_leader": return "Team Leader";
      case "admin": return "Administrator";
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-[#0f2744] z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo Section */}
        <div className="p-6 border-b border-white/10 relative">
          {/* Mobile close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute top-3 right-3 lg:hidden p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="sidebar-close-button"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="flex items-center justify-center">
            <img 
              src={LOGO_URL} 
              alt="CAPIN Realty" 
              className="h-16 w-auto object-contain rounded-lg shadow-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden flex-col items-center">
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CAPIN
              </span>
              <span className="text-[#c9a227] text-sm font-medium tracking-wider">REALTY</span>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#c9a227] to-[#ae8220] flex items-center justify-center shadow-lg">
              <span className="text-lg font-bold text-[#0f2744]">
                {isGuest ? 'G' : (user.full_name?.charAt(0) || "U")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                {isGuest ? "Guest User" : user.full_name}
              </p>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeClass(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-scroll sidebar-scroll">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
              return (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-white/10 text-[#c9a227] border-l-4 border-[#c9a227] shadow-lg"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <item.icon className="h-5 w-5" strokeWidth={1.5} />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg py-3"
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.5} />
            {isGuest ? "Exit Guest Mode" : "Sign Out"}
          </Button>
        </div>

        {/* Footer Branding */}
        <div className="p-4 border-t border-white/10 text-center">
          <p className="text-xs text-slate-400">
            © 2025 CAPIN Realty
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Pune, Maharashtra
          </p>
        </div>
      </aside>

      {/* Notification Panel */}
      {showNotifications && (
        <div className="fixed inset-0 z-50 lg:z-40" onClick={() => setShowNotifications(false)}>
          <div className="absolute inset-0 bg-black/20" />
          <div 
            className="absolute right-4 top-16 lg:right-8 lg:top-4 w-80 max-h-[70vh] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0f2744] text-white p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Notifications</h3>
                <span className="text-xs text-slate-300">{notifications.length} total</span>
              </div>
              {notifications.length > 0 && (
                <div className="flex gap-3">
                  {notificationCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs text-[#c9a227] hover:underline"
                      data-testid="mark-all-read-btn"
                    >
                      Mark All Read
                    </button>
                  )}
                  <button 
                    onClick={clearAllNotifications}
                    className="text-xs text-red-300 hover:underline"
                    data-testid="clear-all-notifications-btn"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      if (!notification.read) markAsRead(notification.id);
                      if (notification.data?.lead_id) {
                        navigate(`/leads/${notification.data.lead_id}`);
                        setShowNotifications(false);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-2 w-2 rounded-full mt-2 ${notification.read ? 'bg-slate-300' : 'bg-[#c9a227]'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#0f2744]">{notification.title}</p>
                        <p className="text-xs text-slate-600 mt-1">{notification.message}</p>
                        {(notification.data?.inquiry_name || notification.data?.inquiry_phone || notification.data?.inquiry_email || notification.data?.preferred_contact || notification.data?.budget_min) && (
                          <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                            {notification.data.inquiry_name && (
                              <p>👤 <span className="font-semibold text-slate-700">{notification.data.inquiry_name}</span></p>
                            )}
                            {notification.data.inquiry_email && (
                              <p>✉️ {notification.data.inquiry_email}</p>
                            )}
                            {notification.data.inquiry_phone && (
                              <p>📱 {notification.data.inquiry_phone}</p>
                            )}
                            {notification.data.preferred_contact && (
                              <p>💬 Prefers: {notification.data.preferred_contact}</p>
                            )}
                            {(notification.data.budget_min || notification.data.budget_max) && (
                              <p>💰 Budget: {notification.data.budget_min ? `₹${Number(notification.data.budget_min).toLocaleString('en-IN')}` : 'N/A'} - {notification.data.budget_max ? `₹${Number(notification.data.budget_max).toLocaleString('en-IN')}` : 'N/A'}</p>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:ml-72">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-6 w-6 text-[#0f2744]" />
            </button>
            
            <img 
              src={LOGO_URL} 
              alt="CAPIN Realty" 
              className="h-10 w-auto object-contain"
            />

            <div className="flex items-center gap-2">
              {/* Notification Bell - Mobile */}
              {!isViewOnlyUser && (
                <button
                  onClick={() => {
                    fetchNotifications();
                    setShowNotifications(!showNotifications);
                  }}
                  className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="notification-bell-mobile"
                >
                  <Bell className="h-5 w-5 text-[#0f2744]" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              )}
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(user.role)}`}>
                {getRoleLabel(user.role)}
              </div>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:block sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center justify-between px-8 py-4">
            <div>
            </div>
            
            <div className="flex items-center gap-4">
              {isGuest && (
                <Button
                  onClick={() => navigate("/login")}
                  className="bg-gradient-to-r from-[#c9a227] to-[#ae8220] text-white font-semibold hover:shadow-lg transition-all rounded-lg"
                  data-testid="sign-in-button"
                >
                  Sign In for Full Access
                </Button>
              )}
              
              {/* Notification Bell - Desktop */}
              {!isViewOnlyUser && (
                <button
                  onClick={() => {
                    fetchNotifications();
                    setShowNotifications(!showNotifications);
                  }}
                  className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="notification-bell-desktop"
                >
                  <Bell className="h-5 w-5 text-[#0f2744]" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              )}
              
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#0f2744]">
                    {isGuest ? "Guest User" : user.full_name}
                  </p>
                  <p className="text-xs text-slate-500">{user.email || "Browse Properties"}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#0f2744] to-[#1e3a5f] flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold text-white">
                    {isGuest ? 'G' : (user.full_name?.charAt(0) || "U")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white mt-8">
          <div className="px-4 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img 
                  src={LOGO_URL} 
                  alt="CAPIN Realty" 
                  className="h-8 w-auto object-contain opacity-80"
                />
                <span className="text-sm text-slate-500">
                  Your Trusted Real Estate Partner in Pune
                </span>
              </div>
              <div className="text-sm text-slate-400">
                © 2025 CAPIN Realty. All rights reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
